/**
 * SVG Drawing module.
 *
 * @module drawing
 * @author timduck@cortona3d.com
 * @copyright (C) 2015-2016 Parallel Graphics Limited. All rights reserved.
 */

require('lib/array-polyfill');

var expand = require('lib/util').expand;
var EventEmitter = require('events').EventEmitter;

var SVGNS = "http://www.w3.org/2000/svg";
var XLINKNS = "http://www.w3.org/1999/xlink";

var SUPPORT_TOUCH = ('ontouchstart' in window);
var SUPPORT_ONLY_TOUCH = SUPPORT_TOUCH && /mobile|tablet|ip(ad|hone|od)|android/i.test(navigator.userAgent);

var Hammer = require('hammerjs');
var pako = require('pako/dist/pako.es5');

var PRESS_TIME = 750;
var DOUBLE_TAP_TIME = 250;

var m_svgHammerManagers = [];
var debounceTimeout = {};

/*
    Members
 */

var m_svgElement,
    m_svgShadow,
    m_shadow = false,
    m_view,
    m_viewportTransform,
    m_viewportTransformShadow,
    m_maxScale = 50,
    m_minScale = 1,
    m_enableDrawingOptimization = false,
    m_textNodesCount = 0,
    m_disableAutoCalculationScaleLimit = false,
    m_enableHoverFilling = false,
    m_isPressed = false,
    m_isDoubleTap = false,
    m_isHidden = true,
    m_isReady = false,
    m_loadingUrl = '',
    m_image = null,
    m_xhr = null;

var m_inertiaStartTime,
    m_velocityX = 0,
    m_velocityY = 0;

// IE11 SVGElement.classList polyfill
(function () {
    // helpers
    var regExp = function (name) {
        return new RegExp('(^| )' + name + '( |$)');
    };
    var forEach = function (list, fn, scope) {
        for (var i = 0; i < list.length; i++) {
            fn.call(scope, list[i]);
        }
    };

    // class list object with basic methods
    function ClassList(element) {
        this.element = element;
    }

    ClassList.prototype = {
        add: function () {
            forEach(arguments, function (name) {
                if (!this.contains(name)) {
                    this.element.className.baseVal += ' ' + name;
                }
            }, this);
        },
        remove: function () {
            forEach(arguments, function (name) {
                this.element.className.baseVal =
                    this.element.className.baseVal.replace(regExp(name), '');
            }, this);
        },
        toggle: function (name, flag) {
            flag = typeof flag === 'undefined' ? !this.contains(name) : flag;
            return (flag ? this.add(name) : this.remove(name)), flag;
        },
        contains: function (name) {
            return regExp(name).test(this.element.className.baseVal);
        }
    };

    if (!('classList' in SVGElement.prototype)) {
        Object.defineProperty(SVGElement.prototype, 'classList', {
            get: function () {
                return new ClassList(this);
            }
        });
    }
})();

/**
 * @private
 * @async
 */
function arrayBufferToString(buffer, encoding) {
    return new Promise(function (resolve, reject) {
        var blob = new Blob([buffer], {
            type: 'text/plain'
        });
        var reader = new FileReader();
        reader.onload = function (e) {
            resolve(e.target.result);
        };
        reader.readAsText(blob, encoding);
    });
}

/**
 * @private
 */
function getHotspotRegion(n) {
    var firstPath,
        res = n.querySelector(':scope > .recognized-aps-region');
    
    if (!res) {
        // changing the order of nodes in SVG <a> element
        // 1. g/path (geometry)
        // 2. path (hotspot region)
        // 3. g/text
        firstPath = n.querySelector(':scope > path');
        if (firstPath) {
            res = firstPath;
            res.classList.add('hotspot-region');
            res.classList.add('recognized-aps-region');
            res.classList.remove('aps-region');
            // fill="#fff" fill-opacity="0"
            res.setAttribute('fill', '#fff');
            res.setAttribute('fill-opacity', '0');
            n.appendChild(res);

            Array.prototype.slice.call(n.getElementsByTagName('text')).forEach(function (text) {
                var textParentNode = text.parentNode;
                if (textParentNode !== n) {
                    if (textParentNode.parentNode === n) {
                        n.appendChild(textParentNode.cloneNode()).appendChild(text);
                        if (!textParentNode.childElementCount) {
                            textParentNode.parentNode.removeChild(textParentNode);
                        }
                    }
                } else {
                    n.appendChild(text);
                }
            });

            // fix: RT-7921 RT-9792
            // move all text nodes from previous sibling group to the anchor
            var previousElementSibling = n.previousElementSibling;
            if (previousElementSibling) {
                var name = n.getAttribute('vm2d:name'),
                    previousSiblingName = previousElementSibling.getAttribute('vm2d:name') || previousElementSibling.textContent.trim();
                if (name && (name === previousSiblingName)) {
                    Array.prototype.slice.call(previousElementSibling.getElementsByTagName('text')).forEach(function (text) {
                        var textParentNode = text.parentNode;
                        n.appendChild(textParentNode.cloneNode()).appendChild(text);
                        if (!textParentNode.childElementCount) {
                            textParentNode.parentNode.removeChild(textParentNode);
                        }
                        if (!previousElementSibling.childElementCount) {
                            previousElementSibling.parentNode.removeChild(previousElementSibling);
                        }
                    });
                }
            }
        }
    }

    return res;
}

/**
 * @private
 */
function getHotspotNodes(n) {
    var el = [],
        hotspotRegion = getHotspotRegion(n);
    if (hotspotRegion) {
        el.push(hotspotRegion);
    } else {
        el = Array.prototype.slice.call(n.querySelectorAll('path, text')).filter(function (node) {
            return getSVGAElement(node) === n;
        });
    }
    return el;
}

/**
 * 
 * @private
 * @param {any} node
 * @returns {SVGAElement}
 */
function getSVGAElement(node) {
    var res = node;
    while (res && (!res.classList || !res.classList.contains('recognized-aps'))) res = res.parentNode;
    return res;
}

var m_eventHandlersA = {
    /**
     * 
     * 
     * @private
     * @param {any} e
     */
    mouseover: function (e) {
        e = e || window.event;
        var target = getSVGAElement(e.target || e.srcElement),
            name = target ? target.getAttribute('name') : '',
            rect = m_view.getBoundingClientRect(),
            offsetX = e.clientX - rect.left,
            offsetY = e.clientY - rect.top;
        SVGDrawing.dispatch("didEnterHotspot", name, offsetX, offsetY);
        e.preventDefault();
    },
    /**
     * 
     * 
     * @private
     * @param {any} e
     */
    mouseout: function (e) {
        e = e || window.event;
        var target = getSVGAElement(e.target || e.srcElement),
            name = target ? target.getAttribute('name') : '',
            rect = m_view.getBoundingClientRect(),
            offsetX = e.clientX - rect.left,
            offsetY = e.clientY - rect.top;
        SVGDrawing.dispatch("didLeaveHotspot", name, offsetX, offsetY);
        e.preventDefault();
    }
};

var m_lastX,
    m_lastY,
    m_lastScale;

var m_eventHandlers = {
    /**
     * 
     * 
     * @private
     * @param {any} e
     */
    contextmenu: function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (m_isPressed) return;
        e = e || window.event;
        var target = e.target || e.srcElement,
            svgANode = getSVGAElement(target),
            name = svgANode ? svgANode.getAttribute('name') : '',
            rect = m_view.getBoundingClientRect(),
            offsetX = e.clientX - rect.left,
            offsetY = e.clientY - rect.top;
        SVGDrawing.dispatch("didCallContextMenu", name, offsetX, offsetY, m_view);
    },

    /**
     * 
     * 
     * @private
     * @param {any} e
     * @returns {any}
     */
    wheel: function (e) {
        e = e || window.event;
        var delta = e.deltaY < 0 ? -0.1 : 0.1;
        scaleBy(delta, e.offsetX, e.offsetY);
        m_velocityX = 0;
        m_velocityY = 0;
        e.preventDefault();
        return false;
    },
    /*
     * 
     * 
     * @private
     * @param {any} e
     * @returns {any}
     */
    dblclick: function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (m_isDoubleTap) return;
        var svg = getSvg();
        var newScale = (m_maxScale - m_minScale) * 0.4 + m_minScale - 0.0001;
        if (svg.currentScale >= newScale) {
            newScale = 1;
        }
        var W = m_view.clientWidth,
            H = m_view.clientHeight;
        function notFarFrom(x, W) {
            return Math.abs(W / 2 - x) / W < 0.2;
        }
        if (newScale === 1 && notFarFrom(e.offsetX, W) && notFarFrom(e.offsetY, H)) {
            reset();
        } else {
            scaleBy(1 - newScale / svg.currentScale, e.offsetX, e.offsetY);
        }
    }
};

/**
 * 
 * 
 * @private
 * @returns {object}
 */
function getSVGDimensions(rect) {
    var bbox = rect;

    if (!bbox) {
        var svg = getSvg();
        bbox = getBoundBox(svg.root);
    }

    var W = m_view.clientWidth || 1,
        H = m_view.clientHeight || 1,
        w = bbox.width || 1,
        h = bbox.height || 1,
        A = W / H,
        a = w / h,
        scale = 1;

    if (A > a) {
        // width
        scale = H / h;
        h = H;
        w = h * a;
    } else if (A < a) {
        // height
        scale = W / w;
        w = W;
        h = w / a;
    } else {
        scale = H / h;
        h = H;
        w = W;
    }
    return {
        x: bbox.x,
        y: bbox.y,
        h: h,
        w: w,
        scale: scale
    };
}

/**
 * @private
 */
function getViewBBox() {
    var svg = getSvg(),
        outerBBox = svg.element.createSVGRect(),
        rect = svg.element.getBoundingClientRect();

    var ctm = svg.element.getScreenCTM();
    var p = svg.element.createSVGPoint();
    p.x = rect.width + rect.x;
    p.y = rect.height + rect.y;
    p = p.matrixTransform(ctm.inverse());
    outerBBox.width = p.x;
    outerBBox.height = p.y;
    p.x = rect.x;
    p.y = rect.y;
    p = p.matrixTransform(ctm.inverse());
    outerBBox.x = p.x;
    outerBBox.y = p.y;
    outerBBox.width -= p.x;
    outerBBox.height -= p.y;
    return outerBBox;
}

/**
 * @private
 * @param {any} x
 * @param {any} y
 */
function setOrigin(x, y) {
    var svg = getSvg(),
        viewBBox = getViewBBox();

    var W = viewBBox.width,
        H = viewBBox.height;

    var svg = getSvg(),
        bbox = svg.element.getBBox(),
        w = bbox.width,
        h = bbox.height;

    var maxOriginX = (W * 0.8 + w) / 2,
        maxOriginY = (H * 0.8 + h) / 2,
        zoomDeltaX = w * (1 - 1 / svg.currentScale) / 2,
        zoomDeltaY = h * (1 - 1 / svg.currentScale) / 2;

    x = Math.max(Math.min(x, maxOriginX - zoomDeltaX), -maxOriginX - zoomDeltaX);
    y = Math.max(Math.min(y, maxOriginY - zoomDeltaY), -maxOriginY - zoomDeltaY);

    return setTransform(x, y);
}

/**
 * 
 * 
 * @param {any} x
 * @param {any} y
 * @param {any} [scale]
 */
var m_shadowTimeout;

function setTransform(x, y, scale) {
    var _apply = function () {
        m_shadowTimeout = null;
        m_svgElement.style.display = 'block';
        m_svgShadow.style.visibility = 'hidden';
        if (m_viewportTransformShadow) {
            var svg = getSvg(m_svgShadow, m_viewportTransformShadow);
            applyTransformTo(m_viewportTransform, svg.currentTranslate.x, svg.currentTranslate.y, svg.currentScale);
        } else {
            applyTransformTo(m_viewportTransform, x, y, scale);
        }
    }
    clearTimeout(m_shadowTimeout);
    applyTransformTo(m_viewportTransformShadow, x, y, scale);
    if (!m_enableDrawingOptimization || !m_shadow) {
        _apply();
    } else {
        if (!m_shadowTimeout) {
            m_svgShadow.style.visibility = 'visible';
            m_svgElement.style.display = 'none';
            var svg = getSvg(m_svgElement, m_viewportTransform);
            applyTransformTo(m_viewportTransformShadow, svg.currentTranslate.x, svg.currentTranslate.y, svg.currentScale);
        }
        m_shadowTimeout = setTimeout(_apply, 1000);
    }
}

/**
 *  
 * @private
 */
function applyTransformTo(viewportTransform, x, y, scale) {
    if (!viewportTransform) return;

    // [a b c d e f] = [currentScale 0 0 currentScale currentTranslate.x currentTranslate.y]
    var m = viewportTransform.matrix,
        oldScale = m.a;

    if (scale !== void 0) {
        m.a = scale;
        m.d = scale;
    }

    m.e = x;
    m.f = y;

    var newScale = m.a;

    if (oldScale !== newScale) {
        SVGDrawing.dispatch("onZoomChange", newScale);
    }
}

function getSvg(srcElement, srcViewportTransform) {
    var element = srcElement,
        viewportTransform = srcViewportTransform,
        m;
    if (!element || !viewportTransform) {
        if (m_enableDrawingOptimization && m_shadowTimeout) {
            element = m_svgShadow;
            viewportTransform = m_viewportTransformShadow;
        } else {
            element = m_svgElement;
            viewportTransform = m_viewportTransform;
        }
    }
    m = viewportTransform.matrix;
    return {
        element: element,
        root: element.lastElementChild,
        transform: viewportTransform,
        currentScale: m.a,
        currentTranslate: {
            x: m.e,
            y: m.f
        }
    };
}

/**
 * 
 * @memberof module:drawing
 * @returns {SVGElement} 
 */
function getSVGSource() {
    var div = document.createElement('div');
    div.appendChild(m_svgElement.cloneNode(true));
    div.firstChild.removeAttribute('id');
    div.firstChild.removeAttribute('style');
    return div.innerHTML;
}

/**
 * @private
 */
function prepareAnchors() {
    var a = m_svgElement.querySelectorAll('g[data-apsname], a[data-apsname]');
    if (!a.length) {
        a = m_svgElement.getElementsByTagName('a');
    }
    for (i = 0; i < a.length; i++) {
        var anchor = a[i],
            href = anchor.getAttribute('xlink:href'),
            type = anchor.getAttribute('vm2d:type') || anchor.dataset.apstype;

        if (href === '#' || href === '') {
            anchor.removeAttribute('xlink:href');
        }

        if (type && type !== 'grobject') continue;

        anchor.classList.add('recognized-aps');

        // In an HTML5 document the attribute has to be accessed with vm2d:name since namespaces are not supported.
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/getAttributeNS#Examples
        var name = anchor.getAttribute('vm2d:name') || anchor.dataset.apsname;

        if (name) {
            anchor.setAttribute('name', name);
        }

        name = anchor.getAttribute('name');
        if (!name) {
            name = anchor.textContent.replace(/[\r\n\s]+/g, ' ').trim();
            if (name) {
                anchor.setAttribute('name', name);
            }
            console.log('WARNING: Element "' + anchor.tagName + '" has no "name" attribute. Uses text content instead: "' + name + '"');
        }
    }

    var anchor;

    while(anchor = m_svgElement.querySelector('.recognized-aps .recognized-aps')) {
        var parentAnchor = getSVGAElement(anchor.parentNode);
        if (parentAnchor) {
            parentAnchor.classList.remove('recognized-aps');
        }
    }
}

/**
 * @private
 */
function revalidateAnchors () {
    m_svgElement.querySelectorAll('a[name]').forEach(function (anchor) {
        anchor.classList.add('recognized-aps');
    })
}

/**
 * @memberof module:drawing
 */
function attach() {
    var eventName,
        mc,
        mcOptions = {},
        i,
        a = m_svgElement.querySelectorAll('.recognized-aps');

    if (!m_isReady) return;
    if (m_svgHammerManagers.length) return;

    if (navigator.pointerEnabled === false) {
        if (SUPPORT_ONLY_TOUCH) {
            mcOptions.inputClass = Hammer.TouchInput;
        } else if (!SUPPORT_TOUCH) {
            mcOptions.inputClass = Hammer.MouseInput;
        } else {
            mcOptions.inputClass = Hammer.TouchMouseInput;
        }
    }

    for (i = 0; i < a.length; i++) {
        (function (anchor) {
            var name = anchor.getAttribute('name');
            if (name) {
                mc = new Hammer.Manager(anchor, expand({
                    recognizers: [
                        [Hammer.Tap]
                    ]
                }, mcOptions));
                mc.on('tap', function (e) {
                    var event = e.srcEvent || e;
                    var keys = +(event.shiftKey && 1) | +(event.ctrlKey && 2) | +(event.altKey && 4);
                    if (!debounceTimeout[name]) {
                        debounceTimeout[name] = setTimeout(function () {
                            delete debounceTimeout[name];
                            var rect = m_view.getBoundingClientRect(),
                                offsetX = event.clientX - rect.left,
                                offsetY = event.clientY - rect.top;
                            SVGDrawing.dispatch("didClickHotspot", name, keys, offsetX, offsetY);
                        }, DOUBLE_TAP_TIME);
                    } else {
                        clearTimeout(debounceTimeout[name]);
                        delete debounceTimeout[name];
                    }
                });
                m_svgHammerManagers.push(mc);

                for (eventName in m_eventHandlersA) {
                    anchor.addEventListener(eventName, m_eventHandlersA[eventName], false);
                }

                if (getHotspotRegion(anchor)) {
                    var n = anchor.firstChild;
                    while (n) {
                        if (n.classList && !n.classList.contains('recognized-aps-region')) {
                            n.setAttribute('pointer-events', 'none');
                        }
                        n = n.nextSibling;
                    }
                }
            }
        })(a[i]);
    }

    mc = new Hammer.Manager(m_view, mcOptions);

    var press = new Hammer.Press({
        time: PRESS_TIME
    });

    var doubletap = new Hammer.Tap({
        event: 'doubletap',
        taps: 2,
        threshold: 5,
        posThreshold: 100,
        interval: DOUBLE_TAP_TIME
    });

    var pinch = new Hammer.Pinch({
        enable: true
    });

    var pan = new Hammer.Pan({
        pointers: 0,
        threshold: 1
    });

    var swipe = new Hammer.Swipe({
        threshold: 5,
        velocity: 0.3
    });

    pinch.recognizeWith(pan);
    swipe.recognizeWith(pan);

    mc.add(press);
    mc.add(doubletap);
    mc.add(pinch);
    mc.add(pan);
    mc.add(swipe);
    mc.set({
        touchAction: 'none'
    });

    mc.on('press', function (e) {
        if (e.pointerType === 'mouse') return;
        m_eventHandlers.contextmenu(e.srcEvent);
        m_isPressed = true;
    });

    mc.on('pressup', function (e) {
        if (e.pointerType === 'mouse') return;
        setTimeout(function () {
            m_isPressed = false;
        }, 250);
    });

    mc.on('doubletap', function (e) {
        m_eventHandlers.dblclick(e.srcEvent);
        m_isDoubleTap = true;
        setTimeout(function () {
            m_isDoubleTap = false;
        }, 250);
    });

    mc.on('pinchstart', function (e) {
        m_lastScale = e.scale;
        m_velocityX = 0;
        m_velocityY = 0;
    });

    mc.on('pinch', function (e) {
        var delta = (m_lastScale - e.scale),
            rect = m_view.getBoundingClientRect(),
            clientX = e.center.x,
            clientY = e.center.y,
            offsetX = clientX - rect.left,
            offsetY = clientY - rect.top;
        m_lastScale = e.scale;
        scaleBy(delta, offsetX, offsetY);
    });

    mc.on('panstart', function (e) {
        m_view.style.cursor = 'move';
        m_lastX = e.deltaX;
        m_lastY = e.deltaY;
        m_velocityX = 0;
        m_velocityY = 0;
    });

    mc.on('pan', function (e) {
        if (e.pointerType === 'mouse' && e.srcEvent.buttons === 0) {
            mc.stop();
            m_view.style.cursor = 'default';
            m_lastX = m_lastY = void 0;
            return;
        }
        if (typeof m_lastX === 'undefined') {
            m_lastX = e.deltaX;
            m_lastY = e.deltaY;
            return;
        }
        shiftBy(e.deltaX - m_lastX, e.deltaY - m_lastY);
        m_lastX = e.deltaX;
        m_lastY = e.deltaY;
    });

    mc.on('panend', function (e) {
        m_view.style.cursor = 'default';
        m_lastX = m_lastY = void 0;
    });

    mc.on('swipe', function (e) {
        if (Cortona3DSolo.app.navigator.inertiaDisabled) return;
        m_velocityX = e.velocityX;
        m_velocityY = e.velocityY;
        m_inertiaStartTime = new Date();
        requestAnimationFrame(inertia);
    });

    m_svgHammerManagers.push(mc);

    for (eventName in m_eventHandlers) {
        m_view.addEventListener(eventName, m_eventHandlers[eventName], false);
    }

    function inertia() {
        var now = new Date(),
            dt = now - m_inertiaStartTime,
            a = (Cortona3DSolo.app.navigator.inertiaDecelerationFactor || 1) * 0.005 * Math.sqrt(m_velocityX * m_velocityX + m_velocityY * m_velocityY),
            ds = a * dt * dt * 0.5,
            dx = (Math.abs(m_velocityX) * dt - ds) * Math.sign(m_velocityX),
            dy = (Math.abs(m_velocityY) * dt - ds) * Math.sign(m_velocityY);
        m_inertiaStartTime = now;
        shiftBy(dx, dy);
        m_velocityX = Math.max(0, Math.abs(m_velocityX) - a * dt) * Math.sign(m_velocityX);
        m_velocityY = Math.max(0, Math.abs(m_velocityY) - a * dt) * Math.sign(m_velocityY);
        if (Math.abs(m_velocityX) < 0.001) m_velocityX = 0;
        if (Math.abs(m_velocityY) < 0.001) m_velocityY = 0;
        if (m_velocityX || m_velocityY) {
            requestAnimationFrame(inertia);
        }
    }
}

/**
 * @memberof module:drawing
 */
function detach() {
    var eventName,
        name,
        anchor,
        i,
        a = m_svgElement.querySelectorAll('.recognized-aps');
    for (i = 0; i < a.length; i++) {
        anchor = a[i];
        if (anchor.__mc) anchor.__mc.destroy();
        delete anchor.__mc;
        for (eventName in m_eventHandlersA) {
            anchor.removeEventListener(eventName, m_eventHandlersA[eventName], false);
        }
    }
    for (eventName in m_eventHandlers) {
        m_view.removeEventListener(eventName, m_eventHandlers[eventName], false);
    }
    m_svgHammerManagers.forEach(function (mc) {
        mc.destroy();
    });
    m_svgHammerManagers.length = 0;
    for (name in debounceTimeout) {
        clearTimeout(debounceTimeout[name]);
    }
    debounceTimeout = {};
    m_zoom = 1;
    m_originX = 0;
    m_originY = 0;
}

/**
 * 
 * @memberof module:drawing
 * @param {string} url
 * @param {boolean} raster
 */
function load(url, raster) {
    /** 
     * @private
     */
    function onLoadSvg(syntax) {
        var svg,
            div = document.createElement('div');

        div.innerHTML = syntax;
        svg = div.querySelector('svg');

        if (!svg) throw new Error('No SVG data.');

        if (svg) {
            reset();
            detach();

            var cssText = m_svgElement.style.cssText;
            var cssClass = m_svgElement.getAttribute('class') || '';

            m_svgElement.parentNode.replaceChild(svg, m_svgElement);
            m_svgElement = svg;
            m_svgElement.setAttribute('touch-action', 'none');
            m_svgElement.style.touchAction = 'none';

            var titleNodes = svg.getElementsByTagName('title');
            for (var i = 0; i < titleNodes.length; i++) {
                titleNodes[i].parentNode.removeChild(titleNodes[i]);
            }

            var childNodes = Array.prototype.slice.call(svg.childNodes || svg.children).filter(function (el) {
                var n = el.nodeName;
                return n !== '#text' && n !== 'title' && n !== 'defs' && n !== 'desc';
            });

            var origin = document.createElementNS(SVGNS, 'g');
            m_viewportTransform = origin.transform.baseVal.initialize(svg.createSVGTransform());

            var root = document.createElementNS(SVGNS, 'g');
            childNodes.forEach(function (node) {
                root.appendChild(node);
            });

            svg.appendChild(origin).appendChild(root);

            if (!svg.getAttribute('viewBox')) {
                var bbox = svg.getBBox(),
                    width = svg.getAttribute('width'),
                    height = svg.getAttribute('height');
                if (!bbox.width && width) bbox.width = width;
                if (!bbox.height && height) bbox.height = height;
                svg.setAttribute('viewBox', bbox.x + ' ' + bbox.y + ' ' + bbox.width + ' ' + bbox.height);
            }

            var viewBox = (svg.getAttribute('viewBox') || '0 0 0 0').split(/\s*[\s,]\s*/);

            v = root.transform.baseVal;
            v.initialize(svg.createSVGTransform());
            v.getItem(0).setTranslate(-viewBox[0], -viewBox[1]);

            viewBox[0] = 0;
            viewBox[1] = 0;
            svg.setAttribute('viewBox', viewBox.join(' '));

            if (!svg.getAttribute('width')) {
                svg.setAttribute('width', viewBox[2]);
            }

            if (!svg.getAttribute('height')) {
                svg.setAttribute('height', viewBox[3]);
            }

            svg.style.cssText = cssText;
            cssClass.split(/\s+/).forEach(function (name) {
                if (name) svg.classList.add(name);
            });

            prepareAnchors();

            m_isReady = true;
            SVGDrawing.dispatch("didCompleteLoad", url);

            revalidateAnchors();

            if (!m_isHidden) {
                detach();
                attach();
            }
            m_loadingUrl = url;

            m_textNodesCount = svg.getElementsByTagName('text').length;

            initShadow();
            calculateScaleLimit();
        }
    }
    if (url == m_loadingUrl) return;
    if (m_xhr) {
        m_xhr.abort();
        m_xhr = null;
    }
    if (m_image) {
        m_image.onload = null;
        m_image = null;
    }
    if (raster || /\.(png|jpe?g|jpe|gif)$/i.test(url)) {
        // PNG, JPEG, GIF
        var img = new Image();
        img.onload = function () {
            unload();

            var image = document.createElementNS(SVGNS, 'image');
            image.setAttribute('image-rendering', 'optimizeQuality');
            image.setAttribute('width', img.width);
            image.setAttribute('height', img.height);
            m_svgElement.setAttribute('viewBox', '0 0 ' + img.width + ' ' + img.height);
            image.setAttributeNS(XLINKNS, 'href', img.src);

            var origin = document.createElementNS(SVGNS, 'g');
            m_viewportTransform = origin.transform.baseVal.initialize(m_svgElement.createSVGTransform());

            m_svgElement.appendChild(origin).appendChild(image);

            calculateScaleLimit();

            m_isReady = true;
            SVGDrawing.dispatch("didCompleteLoad", img.src);

            if (!m_isHidden) attach();
            m_loadingUrl = url;
        };
        img.src = url;
        m_image = img;
    } else {
        // SVG(Z)
        var xhr = new window.XMLHttpRequest();
        if (xhr.overrideMimeType) {
            xhr.overrideMimeType('application/octet-stream'); //application/xhtml+xml
        }
        try {
            var self = this;
            xhr.open("GET", url, true);
            xhr.responseType = 'arraybuffer';
            /**
             * 
             * @private
             */
            xhr.onerror = function () {
                SVGDrawing.dispatch("onProgress", 0, -1);
                SVGDrawing.dispatch("failedLoadingDocument", url, xhr.status);
                m_loadingUrl = '';
            };
            /**
             * 
             * @private
             */
            xhr.onprogress = function (e) {
                var loaded = e.loaded;
                var total = e.lengthComputable || e.lengthComputable === undefined ? e.total : 0;
                SVGDrawing.dispatch("onProgress", loaded, total);
            };
            /**
             * 
             * @private
             */
            xhr.onload = function () {
                SVGDrawing.dispatch("onProgress", 0, -1);
                var binData = new Uint8Array(xhr.response);

                Promise
                    .resolve()
                    .then(function () {
                        /*
                        if (Cortona3DSolo && Cortona3DSolo.app.util.inflate) {
                            var data = Cortona3DSolo.app.util.inflate(binData);
                            if (!data) throw new Error();
                            return arrayBufferToString(new Uint8Array(data))
                                .then(function (str) {
                                    Cortona3DSolo.app.util.freeTypedArray(data);
                                    return str;
                                });
                        }
                        */
                        return arrayBufferToString(new Uint8Array(pako.inflate(binData)));
                    })
                    .catch(function () {
                        return arrayBufferToString(binData);
                    })
                    .then(onLoadSvg)
                    .catch(function () {
                        SVGDrawing.dispatch("failedLoadingDocument", url, xhr.status);
                        m_loadingUrl = '';
                    });
            };
            xhr.send(null);
            m_xhr = xhr;
        } catch (e) {
            SVGDrawing.dispatch("failedLoadingDocument", url, e.message);
            m_loadingUrl = '';
        }
    }
}

/**
 * @memberof module:drawing
 */
function unload() {
    detach();
    while (m_svgElement.firstChild) {
        m_svgElement.removeChild(m_svgElement.firstChild);
    }
    freeShadow();
    m_isReady = false;
    m_loadingUrl = '';
}

function freeShadow() {
    while (m_svgShadow.firstChild) {
        m_svgShadow.removeChild(m_svgShadow.firstChild);
    }
    m_viewportTransformShadow = null;
    m_shadow = false;
}

function initShadow() {
    freeShadow();

    if (m_textNodesCount < 5000 || !m_enableDrawingOptimization) return;

    var svgString = new XMLSerializer().serializeToString(m_svgElement);

    var DOMURL = self.URL || self.webkitURL || self,
        img = new Image(),
        svg = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8"
        }),
        url = DOMURL.createObjectURL(svg);

    img.onload = function () {
        DOMURL.revokeObjectURL(url);

        if (!img.width && !img.height) {
            m_enableDrawingOptimization = false;
            return;
        }

        var canvas = m_view.appendChild(document.createElement('canvas'));

        canvas.style.display = 'none';

        canvas.width = img.width;
        canvas.height = img.height;

        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, img.width, img.height);

        var png = canvas.toDataURL("image/png");
        var image = document.createElementNS(SVGNS, 'image');

        image.setAttribute('image-rendering', 'optimizeQuality');
        image.setAttribute('width', img.width);
        image.setAttribute('height', img.height);
        m_svgShadow.setAttribute('viewBox', '0 0 ' + img.width + ' ' + img.height);
        image.setAttributeNS(XLINKNS, 'href', png);

        var origin = document.createElementNS(SVGNS, 'g');
        m_viewportTransformShadow = origin.transform.baseVal.initialize(m_svgShadow.createSVGTransform());

        m_svgShadow.appendChild(origin).appendChild(image);

        var svg = getSvg(m_svgElement, m_viewportTransform);
        applyTransformTo(m_viewportTransformShadow, svg.currentTranslate.x, svg.currentTranslate.y, svg.currentScale);

        m_view.removeChild(canvas);
        m_shadow = true;
    };

    img.src = url;
}

function calculateScaleLimit() {
    if (m_disableAutoCalculationScaleLimit) return;
    try {
        var viewBox = m_svgElement.getBBox();
        m_maxScale = Math.sqrt(viewBox.width * viewBox.width + viewBox.height * viewBox.height) / 50 || 50;
    } catch (e) { }
}

/**
 * 
 * @memberof module:drawing
 * @param {any} hotspotName
 * @param {any} className
 * @param {any} flag
 * @returns {boolean}
 */
function toggleHotspotClass(hotspotName, className, flag) {
    var res = flag;
    getObjectsWithName(hotspotName)
        .flatMap(getHotspotNodes)
        .forEach(function (path) {
            if (path.classList) {
                res = path.classList.toggle(className, flag);
            }
        });
    return res;
}

/**
 * @memberof module:drawing
 */
function reset() {
    setTransform(0, 0, 1);
}

/**
 * 
 * @memberof module:drawing
 * @param {any} delta
 */
function scaleBy(delta, offsetX, offsetY) {
    var svg = getSvg();
    var scale = svg.currentScale;
    var newScale = Math.max(m_minScale, Math.min(m_maxScale, scale * (1 - delta)));

    var W = m_view.clientWidth,
        H = m_view.clientHeight;

    offsetX = (offsetX === void 0 ? W / 2 : offsetX);
    offsetY = (offsetY === void 0 ? H / 2 : offsetY);

    var p = svg.element.createSVGPoint();
    p.x = offsetX;
    p.y = offsetY;
    p = p.matrixTransform(svg.root.getCTM().inverse());

    var m = svg.transform.matrix.translate(p.x, p.y).scale(newScale / scale).translate(-p.x, -p.y);

    setTransform(m.e, m.f, newScale);
}

/**
 * 
 * @memberof module:drawing
 * @param {any} deltaX
 * @param {any} deltaY
 */
function shiftBy(deltaX, deltaY) {
    if (!deltaX && !deltaY) return;
    var svg = getSvg();
    var ctm = svg.root.getCTM();
    var p = svg.element.createSVGPoint();
    p.x = svg.currentTranslate.x;
    p.y = svg.currentTranslate.y;
    p = p.matrixTransform(ctm);
    p.x += deltaX * svg.currentScale;
    p.y += deltaY * svg.currentScale;
    p = p.matrixTransform(ctm.inverse());
    setOrigin(p.x, p.y);
}

/**
 * @private
 * @param {*} name 
 * @returns
 */

function getObjectsWithName(name) {
    if (!m_svgElement) return [];
    var n = String(name).valueOf();
    var a = Array.prototype.slice.call(m_svgElement.querySelectorAll('.recognized-aps'))
        .filter(function (a) {
            return a.getAttribute('name') === n;
        });
    return a;
}

function getBoundBox(node) {
    var res;
    var svgElement = getSvg().element;
    switch (typeof node) {
        case 'undefined':
            res = getBoundBox(svgElement);
            break;
        case 'object':
            if (Array.isArray(node)) {
                node
                    .flatMap(function (item) {
                        if (typeof item === 'string') {
                            return getObjectsWithName(item);
                        }
                        return item;
                    })
                    .flatMap(getHotspotNodes)
                    .forEach(function (node) {
                        var bbox = getBoundBox(node);
                        if (!res) {
                            res = svgElement.createSVGRect();
                            res.x = bbox.x;
                            res.y = bbox.y;
                        }
                        res.width = Math.max(res.x + res.width, bbox.x + bbox.width);
                        res.height = Math.max(res.y + res.height, bbox.y + bbox.height);
                        res.x = Math.min(res.x, bbox.x);
                        res.y = Math.min(res.y, bbox.y);
                        res.width -= res.x;
                        res.height -= res.y;
                    })
            } else {
                var bbox = node.getBBox();
                var p = svgElement.createSVGPoint();
                var p2 = svgElement.createSVGPoint();
                var ctm = node.getCTM();
                p.x = bbox.x;
                p.y = bbox.y;
                p2.x = bbox.x + bbox.width;
                p2.y = bbox.y + bbox.height;
                p = p.matrixTransform(ctm);
                bbox.x = p.x;
                bbox.y = p.y;
                p2 = p2.matrixTransform(ctm);
                bbox.width = p2.x - p.x;
                bbox.height = p2.y - p.y;
                res = bbox;
            }
            break;
        case 'string':
            res = getBoundBox([node]);
            break;
        default:
            res = getBoundBox(String(node).valueOf());
    }
    return res;
}

/**
 * 
 * @memberof module:drawing
 * @param {any} hotspotName
 */
function centerTo(hotspotName) {
    var bbox = getBoundBox(hotspotName);
    if (!bbox) return;

    var W = m_view.clientWidth,
        H = m_view.clientHeight;

    var hotspot_x = (bbox.x + bbox.width / 2),
        hotspot_y = (bbox.y + bbox.height / 2);

    var dx = W / 2 - hotspot_x;
    var dy = H / 2 - hotspot_y;

    shiftBy(dx, dy);
}

/**
 * 
 * @memberof module:drawing
 * @param {any} hotspotName
 */
function fitTo(hotspotName, factor) {
    centerTo(hotspotName);

    factor = factor || 0.5;

    var bbox = getBoundBox(hotspotName);
    if (!bbox) return;

    var svg = getSvg();

    var dim = getSVGDimensions(bbox),
        newScale = svg.currentScale * dim.scale * factor;

    scaleBy(1 - newScale / svg.currentScale);
}

/**
 * @memberof module:drawing
 */
function hide() {
    detach();
    m_view.style.visibility = 'hidden';
    m_isHidden = true;
}

/**
 * @memberof module:drawing
 */
function show() {
    attach();
    m_view.style.visibility = 'visible';
    m_isHidden = false;
    calculateScaleLimit();
}

/**
 * @memberof module:drawing
 * @param {HTMLElement} svgElement
 * @param {object} [options]
 */
function initialize(svgElement, options) {
    svgElement.setAttribute('touch-action', 'none');
    svgElement.style.touchAction = 'none';
    svgElement.style.width = '100%';
    svgElement.style.height = '100%';
    svgElement.style.position = 'absolute';
    svgElement.style.display = 'block';
    svgElement.style.overflow = 'hidden';
    m_svgElement = svgElement;
    m_view = document.createElement('div');
    m_view.classList.add('cortona3dsolo-svg');
    svgElement.parentNode.replaceChild(m_view, svgElement);
    m_view.appendChild(svgElement);
    m_svgShadow = m_view.appendChild(document.createElementNS(SVGNS, 'svg'));
    m_svgShadow.setAttribute('touch-action', 'none');
    m_svgShadow.style.touchAction = 'none';
    m_svgShadow.style.pointerEvents = 'none';
    m_svgShadow.style.width = '100%';
    m_svgShadow.style.height = '100%';
    m_svgShadow.style.position = 'absolute';
    svgElement.style.display = 'block';
    m_svgShadow.style.overflow = 'hidden';
    m_svgShadow.style.visibility = 'hidden';
    hide();
}

var SVGDrawing = expand(new EventEmitter(), {
    dispatch: function (eventName) {
        SVGDrawing.emit.apply(SVGDrawing, arguments);
        if (typeof SVGDrawing.delegate[eventName] == 'function') {
            SVGDrawing.delegate[eventName].apply(SVGDrawing, Array.prototype.slice.call(arguments, 1));
        }
    },
    initialize: initialize, // initialize(svgElement, options)
    hide: hide,
    show: show,
    reset: reset,
    scaleBy: scaleBy,
    shiftBy: shiftBy,
    centerTo: centerTo,
    fitTo: fitTo,
    toggleHotspotClass: toggleHotspotClass,
    load: load,
    unload: unload,
    attach: attach,
    detach: detach,
    getSVGSource: getSVGSource,
    getObjectsWithName: getObjectsWithName,
    /**
     * @returns {boolean}
     */
    checkComplete: function () {
        return m_isReady;
    },
    /**
     * @returns {boolean}
     */
    checkShown: function () {
        return !m_isHidden;
    },
    addObjectsWithClassName: function (names, className) {
        (Array.isArray(names) ? names : [names])
            .forEach(function (name) { toggleHotspotClass(name, className, true); });
    },
    setObjectsWithClassName: function (names, className) {
        m_svgElement.querySelectorAll('.' + className)
            .forEach(function (node) { return node.classList.remove(className); });
        (Array.isArray(names) ? names : [names])
            .forEach(function (name) { return toggleHotspotClass(name, className, true); });
    },
    removeObjectsWithClassName: function (names, className) {
        (Array.isArray(names) ? names : [names])
            .forEach(function (name) { return toggleHotspotClass(name, className, false); });
    },
    getObjectsWithClassName: function (className) {
        var res = [];
        m_svgElement.querySelectorAll('.' + className)
            .forEach(function (node) {
                var name = node.getAttribute('name') || node.parentNode.getAttribute('name');
                if (typeof name === 'string' && res.indexOf(name) < 0) {
                    res.push(name);
                }
            });
        return res;
    },
    /**
     * Hooks
     */
    delegate: {} // hooks
    /*
     didClickHotspot(name) 
     didEnterHotspot(name)
     didLeaveHotspot(name)
     didCompleteLoad(url)
     didCallContextMenu(name, offsetX, offsetY, target)
     failedLoadingDocument()
     onProgress(loaded, total)
     onZoomChange(zoom)
     */
});

Object.defineProperties(SVGDrawing, {
    'svg': {
        get: function () {
            return m_svgElement;
        },
        enumerable: true
    },
    'enableDrawingOptimization': {
        get: function () {
            return m_enableDrawingOptimization;
        },
        set: function (value) {
            m_enableDrawingOptimization = !!value;
        },
        enumerable: true
    },
    'enableHoverFilling': {
        get: function () {
            return m_enableHoverFilling;
        },
        set: function (value) {
            m_enableHoverFilling = !!value;
        },
        enumerable: true
    },
    'maxScale': {
        get: function () {
            return m_maxScale;
        },
        set: function (scale) {
            m_disableAutoCalculationScaleLimit = !!scale;
            m_maxScale = Math.max(+scale > 0 ? scale : 50, m_minScale);
        },
        enumerable: true
    },
    'minScale': {
        get: function () {
            return m_minScale;
        },
        set: function (scale) {
            m_minScale = Math.min(+scale > 0 ? scale : 1, m_maxScale);
        },
        enumerable: true
    }
});

module.exports = SVGDrawing;